import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Heart, MessageSquare, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Forum() {
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [posts, setPosts] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openPost, setOpenPost] = useState(null);

  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newCat, setNewCat] = useState("");
  const [anonymous, setAnonymous] = useState(false);

  const loadPosts = async () => {
    const q = activeCat && activeCat !== "All" ? `?category=${encodeURIComponent(activeCat)}` : "";
    const { data } = await api.get(`/forum/posts${q}`);
    setPosts(data);
  };

  useEffect(() => {
    api.get("/forum/categories").then(({ data }) => setCategories(data));
  }, []);

  useEffect(() => { loadPosts(); }, [activeCat]);

  const createPost = async () => {
    if (!newTitle || !newBody || !newCat) return;
    try {
      await api.post("/forum/posts", { title: newTitle, body: newBody, category: newCat, anonymous });
      toast.success("Post shared with the community");
      setOpenCreate(false);
      setNewTitle(""); setNewBody(""); setNewCat(""); setAnonymous(false);
      loadPosts();
    } catch {
      toast.error("Couldn't post. Try again.");
    }
  };

  const openPostDetail = async (postId) => {
    const { data } = await api.get(`/forum/posts/${postId}`);
    setOpenPost(data);
  };

  const heart = async (postId) => {
    await api.post(`/forum/posts/${postId}/heart`);
    loadPosts();
  };

  const [commentText, setCommentText] = useState("");
  const addComment = async () => {
    if (!commentText.trim() || !openPost) return;
    await api.post(`/forum/posts/${openPost.post_id}/comments`, { body: commentText });
    setCommentText("");
    openPostDetail(openPost.post_id);
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C2623]">Community</h1>
            <p className="text-[#6E655F] mt-2">Wisdom, warmth, and stories from folks living beautifully with HSV.</p>
          </div>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button data-testid="forum-create-post-btn"
                className="rounded-full bg-[#D95D39] hover:bg-[#C24C29] gap-2">
                <Plus className="w-4 h-4" /> Share something
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Share with the community</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Select value={newCat} onValueChange={setNewCat}>
                    <SelectTrigger data-testid="forum-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Title</Label>
                  <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                    data-testid="forum-title-input" placeholder="What's on your mind?" />
                </div>
                <div className="space-y-1.5">
                  <Label>Your story</Label>
                  <Textarea value={newBody} onChange={(e) => setNewBody(e.target.value)}
                    data-testid="forum-body-input" rows={5}
                    placeholder="Share thoughts, experiences, questions — anything." />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="forum-anonymous-toggle" />
                  <Label className="text-sm">Post anonymously</Label>
                </div>
                <Button onClick={createPost} data-testid="forum-submit-post-btn"
                  className="w-full rounded-full bg-[#D95D39] hover:bg-[#C24C29]">Publish</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["All", ...categories].map((c) => (
            <button key={c} onClick={() => setActiveCat(c)}
              data-testid={`forum-category-${c.replace(/\s|&/g, "-")}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeCat === c ? "bg-[#2A524A] text-white" : "bg-white text-[#2C2623] border border-[#E8DEC8] hover:bg-[#F9F5EE]"
              }`}>
              {c}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {posts.length === 0 && (
            <Card className="p-10 text-center rounded-3xl border-[#E8DEC8] bg-white">
              <p className="text-[#6E655F]">No posts here yet. Be the first to share.</p>
            </Card>
          )}
          {posts.map((p) => (
            <Card key={p.post_id} data-testid={`forum-post-${p.post_id}`}
              className="p-6 rounded-2xl border-[#E8DEC8] bg-white hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="w-9 h-9">
                  {p.author_picture && <AvatarImage src={p.author_picture} />}
                  <AvatarFallback className="bg-[#2A524A] text-white text-sm">{p.author_name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium text-sm text-[#2C2623]">{p.author_name}</div>
                  <div className="text-xs text-[#6E655F]">{new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                <Badge className="ml-auto bg-[#EAF3F0] text-[#2A524A] hover:bg-[#EAF3F0]">{p.category}</Badge>
              </div>
              <h3 onClick={() => openPostDetail(p.post_id)}
                className="font-serif text-xl font-semibold text-[#2C2623] mb-2 cursor-pointer hover:text-[#D95D39]">
                {p.title}
              </h3>
              <p className="text-[#6E655F] leading-relaxed line-clamp-3">{p.body}</p>
              <div className="flex items-center gap-4 mt-4 text-sm text-[#6E655F]">
                <button onClick={() => heart(p.post_id)} data-testid={`forum-heart-${p.post_id}`}
                  className="flex items-center gap-1 hover:text-[#D95D39]">
                  <Heart className="w-4 h-4" /> {p.hearts}
                </button>
                <button onClick={() => openPostDetail(p.post_id)} className="flex items-center gap-1 hover:text-[#2A524A]">
                  <MessageSquare className="w-4 h-4" /> {p.comment_count}
                </button>
              </div>
            </Card>
          ))}
        </div>

        <Dialog open={!!openPost} onOpenChange={(v) => !v && setOpenPost(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            {openPost && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">{openPost.title}</DialogTitle>
                </DialogHeader>
                <div className="flex items-center gap-3 -mt-2">
                  <Avatar className="w-8 h-8">
                    {openPost.author_picture && <AvatarImage src={openPost.author_picture} />}
                    <AvatarFallback className="bg-[#2A524A] text-white text-xs">{openPost.author_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-[#6E655F]">{openPost.author_name} · {new Date(openPost.created_at).toLocaleDateString()}</span>
                  <Badge className="ml-auto bg-[#EAF3F0] text-[#2A524A]">{openPost.category}</Badge>
                </div>
                <p className="text-[#2C2623] leading-relaxed whitespace-pre-wrap">{openPost.body}</p>
                <div className="border-t border-[#E8DEC8] pt-4 space-y-3">
                  <div className="font-semibold text-sm text-[#2C2623]">Comments ({openPost.comments?.length || 0})</div>
                  {openPost.comments?.map((c) => (
                    <div key={c.comment_id} className="flex gap-3">
                      <Avatar className="w-8 h-8">
                        {c.author_picture && <AvatarImage src={c.author_picture} />}
                        <AvatarFallback className="bg-[#D95D39] text-white text-xs">{c.author_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 bg-[#F9F5EE] rounded-2xl p-3">
                        <div className="text-sm font-medium text-[#2C2623]">{c.author_name}</div>
                        <div className="text-sm text-[#2C2623]">{c.body}</div>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <Input value={commentText} onChange={(e) => setCommentText(e.target.value)}
                      data-testid="forum-comment-input" placeholder="Share support…" />
                    <Button onClick={addComment} data-testid="forum-comment-submit"
                      className="rounded-full bg-[#2A524A] hover:bg-[#1F3E37]">Send</Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
